import { lazy } from 'react'
import generateRouterDetail from 'utils/helpers/generateRouterDetail'
import { limitSkuPageRBAC } from 'features/SupplyOrder/pages/LimitSKU/rbac'
import { ReplenishmentTaskPageRBAC } from 'features/SupplyOrder/pages/ReplenishmentTask/rbac'
import { ReplenishmentTaskDetailPageRBAC } from 'features/SupplyOrder/pages/ReplenishmentTaskDetail/rbac'
import { ReplenishmentTaskPartialPageRBAC } from 'features/SupplyOrder/pages/ReplenishmentTaskPartial/rbac'
import { ReplenishmentConfigPageRBAC } from 'features/SupplyOrder/pages/ReplenishmentConfig/rbac'
import { InboundSupplyOrderCheckQtyRBAC } from 'pages/InboundSoCheckQty/rbac'
import { XdockRBAC } from '../pages/Xdock/rbac'
import { XdockV2RBAC } from '../pages/XdockV2/rbac'
import { ReplenishmentScheduleRBAC } from '../pages/ReplenishmentSchedule/rbac'
import { GenerateAssetRBAC } from '../pages/GenerateAssetId/rbac'
import { GeneratePackageIDRBAC } from '../pages/GeneratePackageId/rbac'
import { GeneratePackageIdDetailRBAC } from '../pages/GeneratePackageIdDetail/rbac'
import { ReplenishmentLetdownPageRBAC } from '../pages/ReplenishmentLetdown/rbac'
import {
  ReplenishmentLetdownDetailPageRBAC,
  ReplenishmentPutawayDetailPageRBAC,
} from '../pages/ReplenishmentLetdownDetail/rbac'
import { ReplenishmentPutawayPageRBAC } from '../pages/ReplenishmentPutaway/rbac'
import { InboundSupplyOrderUnloadingTypeRBAC } from '../pages/InboundUnloadingDetail/rbac'
import OutboundSODetail from '../pages/OutboundSODetail'
import OutboundSOAddEdit from '../pages/OutboundSOAddEdit'
import OutboundSOAddProduct from '../pages/OutboundSOAddProduct'
import OutboundSOAddProductDirect from '../pages/OutboundSOAddProductDirect'
import { OutboundSORBAC } from '../pages/OutboundSO/rbac'
import { OutboundSOAddProductDirectRBAC } from '../pages/OutboundSOAddProductDirect/rbac'
import { OutboundSOAddProductRBAC } from '../pages/OutboundSOAddProduct/rbac'
import { OutboundSODetailRBAC } from '../pages/OutboundSODetail/rbac'
import { PutawayHubPageRBAC } from '../pages/PutawayHub/rbac'
import { OutboundSKUConfigRBAC } from '../pages/OutboundSKUConfig/rbac'

const ReplenishmentTask = lazy(() => import('../pages/ReplenishmentTask'))
const ReplenishmentTaskDetail = lazy(() => import('../pages/ReplenishmentTaskDetail'))
const ReplenishmentTaskPartial = lazy(() => import('../pages/ReplenishmentTaskPartial'))
const ReplenishmentSchedule = lazy(() => import('../pages/ReplenishmentSchedule'))
const ReplenishmentConfig = lazy(() => import('../pages/ReplenishmentConfig'))
const InboundUnloadingDetail = lazy(() => import('../pages/InboundUnloadingDetail'))
const LimitSKUPage = lazy(() => import('../pages/LimitSKU'))
const Xdock = lazy(() => import('../pages/Xdock'))
const XdockV2 = lazy(() => import('../pages/XdockV2'))
const GenerateAssetId = lazy(() => import('../pages/GenerateAssetId'))
const GeneratePackageId = lazy(() => import('../pages/GeneratePackageId'))
const GeneratePackageIdDetail = lazy(() => import('../pages/GeneratePackageIdDetail'))
const ReplenishmentLetdown = lazy(() => import('../pages/ReplenishmentLetdown'))
const ReplenishmentLetdownDetail = lazy(() => import('../pages/ReplenishmentLetdownDetail'))
const ReplenishmentPutaway = lazy(() => import('../pages/ReplenishmentPutaway'))
const OutboundSO = lazy(() => import('../pages/OutboundSO'))
const PutawayHub = lazy(() => import('../pages/PutawayHub'))
const InboundHubCheckQuantityPage = lazy(
  () => import('../pages/InboundCheckQuantity/HubInboundCheckQuantity'),
)
const PrintPreviewGRN = lazy(() => import('../pages/PrintPreviewGRN'))
const OutboundSKUConfig = lazy(() => import('../pages/OutboundSKUConfig'))

type NamedRoutesType =
  | 'ReplenishmentTaskRouter'
  | 'ReplenishmentTaskDetailRouter'
  | 'ReplenishmentTaskPartialRouter'
  | 'ReplenishmentScheduleRouter'
  | 'ReplenishmentConfigRouter'
  | 'InboundUnloadingDetailRouter'
  | 'LimitSKURouter'
  | 'XdockRouter'
  | 'XdockV2Router'
  | 'AssetIdGeneratorRouter'
  | 'PackageIdGeneratorRouter'
  | 'PackageIdGeneratorDetailRouter'
  | 'ReplenishmentLetdownRouter'
  | 'ReplenishmentLetdownDetailRouter'
  | 'ReplenishmentPutawayRouter'
  | 'ReplenishmentPutawayDetailRouter'
  | 'OutboundSORouter'
  | 'OutboundSODetailRouter'
  | 'OutboundSOAddRouter'
  | 'OutboundSOEditRouter'
  | 'OutboundSOAddProductRouter'
  | 'OutboundSOAddProductDirectRouter'
  | 'HubInboundCheckQuantityRouter'
  | 'PutawayHubRouter'
  | 'PrintPreviewGRNRouter'
  | 'OutboundSKUConfigRouter'

type PathListsType =
  | '/dashboard/replenishment-task'
  | '/dashboard/replenishment-task/:id'
  | '/dashboard/replenishment-task/partial/:id'
  | '/dashboard/replenishment-schedule'
  | '/dashboard/replenishment-config'
  | '/dashboard/inbound-so/unloading/:id'
  | '/dashboard/limit-sku'
  | '/dashboard/xdock'
  | '/dashboard/outbound/xdock'
  | '/dashboard/asset-id-generator'
  | '/dashboard/package-id-generator'
  | '/dashboard/package-id-generator/details'
  | '/dashboard/replenishment-letdown'
  | '/dashboard/replenishment-letdown/:id'
  | '/dashboard/replenishment-putaway'
  | '/dashboard/replenishment-putaway/:id'
  | '/dashboard/outbound-so'
  | '/dashboard/outbound-so/detail/:id'
  | '/dashboard/outbound-so/add'
  | '/dashboard/outbound-so/edit/:id'
  | '/dashboard/outbound-so/add-product'
  | '/dashboard/outbound-so/add-product-direct'
  | '/dashboard/inbound-so/check-quantity/hub/:id'
  | '/dashboard/putaway-hub'
  | '/dashboard/inbound-so/print-preview-grn/:id'
  | '/dashboard/outbound-sku-config'

type RouterInstanceType = RouterFeatureInterface<NamedRoutesType, PathListsType>

const ReplenishmentTaskRouter: RouterInstanceType = {
  name: 'ReplenishmentTaskRouter',
  Component: ReplenishmentTask,
  path: '/dashboard/replenishment-task',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: ReplenishmentTaskPageRBAC.pageID,
  isDashboard: true,
}

const ReplenishmentTaskDetailRouter: RouterInstanceType = {
  name: 'ReplenishmentTaskDetailRouter',
  Component: ReplenishmentTaskDetail,
  path: '/dashboard/replenishment-task/:id',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: ReplenishmentTaskDetailPageRBAC.pageID,
  isDashboard: true,
}

const ReplenishmentTaskPartialRouter: RouterInstanceType = {
  name: 'ReplenishmentTaskPartialRouter',
  Component: ReplenishmentTaskPartial,
  path: '/dashboard/replenishment-task/partial/:id',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: ReplenishmentTaskPartialPageRBAC.pageID,
  isDashboard: true,
}

const ReplenishmentScheduleRouter: RouterInstanceType = {
  name: 'ReplenishmentScheduleRouter',
  Component: ReplenishmentSchedule,
  path: '/dashboard/replenishment-schedule',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: ReplenishmentScheduleRBAC.pageID,
  isDashboard: true,
}

const ReplenishmentConfigRouter: RouterInstanceType = {
  name: 'ReplenishmentConfigRouter',
  Component: ReplenishmentConfig,
  path: '/dashboard/replenishment-config',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: ReplenishmentConfigPageRBAC.pageID,
  isDashboard: true,
}

const UnloadingDetailRouter: RouterInstanceType = {
  name: 'InboundUnloadingDetailRouter',
  Component: InboundUnloadingDetail,
  path: '/dashboard/inbound-so/unloading/:id',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: InboundSupplyOrderUnloadingTypeRBAC.pageID,
  isDashboard: true,
}

const LimitSKURouter: RouterInstanceType = {
  name: 'LimitSKURouter',
  Component: LimitSKUPage,
  path: '/dashboard/limit-sku',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: limitSkuPageRBAC.pageID,
  isDashboard: true,
}

const XdockRouter: RouterInstanceType = {
  name: 'XdockRouter',
  Component: Xdock,
  path: '/dashboard/xdock',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: XdockRBAC.pageID,
  isDashboard: true,
}

const XdockV2Router: RouterInstanceType = {
  name: 'XdockV2Router',
  Component: XdockV2,
  path: '/dashboard/outbound/xdock',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: XdockV2RBAC.pageID,
  isDashboard: true,
}

const AssetIdGeneratorRouter: RouterInstanceType = {
  name: 'AssetIdGeneratorRouter',
  Component: GenerateAssetId,
  path: '/dashboard/asset-id-generator',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: GenerateAssetRBAC.pageID,
  isDashboard: true,
}

const PackageIdGeneratorRouter: RouterInstanceType = {
  name: 'PackageIdGeneratorRouter',
  Component: GeneratePackageId,
  path: '/dashboard/package-id-generator',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: GeneratePackageIDRBAC.pageID,
  isDashboard: true,
}

const PackageIdGeneratorDetailRouter: RouterInstanceType = {
  name: 'PackageIdGeneratorDetailRouter',
  Component: GeneratePackageIdDetail,
  path: '/dashboard/package-id-generator/details',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: GeneratePackageIdDetailRBAC.pageID,
  isDashboard: true,
}

const ReplenishmentLetdownRouter: RouterInstanceType = {
  name: 'ReplenishmentLetdownRouter',
  Component: ReplenishmentLetdown,
  path: '/dashboard/replenishment-letdown',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: ReplenishmentLetdownPageRBAC.pageID,
  isDashboard: true,
}

const ReplenishmentLetdownDetailRouter: RouterInstanceType = {
  name: 'ReplenishmentLetdownDetailRouter',
  Component: ReplenishmentLetdownDetail,
  path: '/dashboard/replenishment-letdown/:id',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: ReplenishmentLetdownDetailPageRBAC.pageID,
  isDashboard: true,
}

const ReplenishmentPutawayRouter: RouterInstanceType = {
  name: 'ReplenishmentPutawayRouter',
  Component: ReplenishmentPutaway,
  path: '/dashboard/replenishment-putaway',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: ReplenishmentPutawayPageRBAC.pageID,
  isDashboard: true,
}

const ReplenishmentPutawayDetailRouter: RouterInstanceType = {
  name: 'ReplenishmentPutawayDetailRouter',
  Component: ReplenishmentLetdownDetail,
  path: '/dashboard/replenishment-putaway/:id',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: ReplenishmentPutawayDetailPageRBAC.pageID,
  isDashboard: true,
}

const OutboundSORouter: RouterInstanceType = {
  name: 'OutboundSORouter',
  Component: OutboundSO,
  path: '/dashboard/outbound-so',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: OutboundSORBAC.pageID,
  isDashboard: true,
}

const OutboundSODetailRouter: RouterInstanceType = {
  name: 'OutboundSODetailRouter',
  Component: OutboundSODetail,
  path: '/dashboard/outbound-so/detail/:id',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: OutboundSODetailRBAC.pageID,
  isDashboard: true,
}

const OutboundSOAddRouter: RouterInstanceType = {
  name: 'OutboundSOAddRouter',
  Component: OutboundSOAddEdit,
  path: '/dashboard/outbound-so/add',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: OutboundSODetailRBAC.pageID,
  isDashboard: true,
}

const OutboundSOEditRouter: RouterInstanceType = {
  name: 'OutboundSOEditRouter',
  Component: OutboundSOAddEdit,
  path: '/dashboard/outbound-so/edit/:id',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: OutboundSODetailRBAC.pageID,
  isDashboard: true,
}

const OutboundSOAddProductRouter: RouterInstanceType = {
  name: 'OutboundSOAddProductRouter',
  Component: OutboundSOAddProduct,
  path: '/dashboard/outbound-so/add-product',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: OutboundSOAddProductRBAC.pageID,
  isDashboard: true,
}

const OutboundSOAddProductDirectRouter: RouterInstanceType = {
  name: 'OutboundSOAddProductDirectRouter',
  Component: OutboundSOAddProductDirect,
  path: '/dashboard/outbound-so/add-product-direct',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: OutboundSOAddProductDirectRBAC.pageID,
  isDashboard: true,
}

const PutawayHubRouter: RouterInstanceType = {
  name: 'PutawayHubRouter',
  Component: PutawayHub,
  path: '/dashboard/putaway-hub',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: PutawayHubPageRBAC.pageID,
  isDashboard: true,
}

const HubInboundCheckQuantityRouter: RouterInstanceType = {
  name: 'HubInboundCheckQuantityRouter',
  Component: InboundHubCheckQuantityPage,
  path: '/dashboard/inbound-so/check-quantity/hub/:id',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: InboundSupplyOrderCheckQtyRBAC.pageID,
  isDashboard: true,
}

const PrintPreviewGRNRouter: RouterInstanceType = {
  name: 'PrintPreviewGRNRouter',
  Component: PrintPreviewGRN,
  path: '/dashboard/inbound-so/print-preview-grn/:id',
  routeType: 'PRIVATE',
  allowedRoles: [],
  isDashboard: false,
}

const OutboundSKUConfigRouter: RouterInstanceType = {
  name: 'OutboundSKUConfigRouter',
  Component: OutboundSKUConfig,
  path: '/dashboard/outbound-sku-config',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: OutboundSKUConfigRBAC.pageID,
  isDashboard: true,
}

const SupplyOrderRouter = [
  ReplenishmentTaskPartialRouter,
  ReplenishmentTaskDetailRouter,
  ReplenishmentTaskRouter,
  ReplenishmentScheduleRouter,
  ReplenishmentConfigRouter,
  UnloadingDetailRouter,
  LimitSKURouter,
  XdockRouter,
  XdockV2Router,
  AssetIdGeneratorRouter,
  PackageIdGeneratorRouter,
  PackageIdGeneratorDetailRouter,
  ReplenishmentLetdownRouter,
  ReplenishmentLetdownDetailRouter,
  ReplenishmentPutawayRouter,
  ReplenishmentPutawayDetailRouter,
  OutboundSORouter,
  OutboundSODetailRouter,
  OutboundSOAddRouter,
  OutboundSOEditRouter,
  OutboundSOAddProductRouter,
  OutboundSOAddProductDirectRouter,
  HubInboundCheckQuantityRouter,
  PutawayHubRouter,
  PrintPreviewGRNRouter,
  OutboundSKUConfigRouter,
]

export const SupplyOrderRouterDetail = generateRouterDetail(SupplyOrderRouter)

export default SupplyOrderRouter
